<template>
  <div id="app">
    <header class="header">
      <div class="container">
        <div class="header-content">
          <div class="logo">
            <h1>ScaleOPT</h1>
          </div>
          <nav class="nav">
            <a href="#speakers" class="nav-link">Speakers</a>
            <a href="#schedule" class="nav-link">Schedule</a>
            <a href="#submission" class="nav-link">Call for Papers</a>
            <a href="#organizers" class="nav-link">Organizers</a>
            <a href="#program-committee" class="nav-link">Program Committee</a>
          </nav>
        </div>
      </div>
    </header>
    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 标题部分 -->
      <section class="hero-section">
        <div class="container">
          <div class="hero-content">
            <h1 class="hero-title">ScaleOPT: GPU-Accelerated and Scalable Optimization</h1>
            <h2 class="hero-subtitle">NeurIPS 2025 Workshop</h2>
            <h3 class="hero-subtitle">Upper Level Room 9: Dec 7, 2025</h3>
            <p class="hero-description" style="font-size:23px">
              Recent GPU hardware and AI system advancement has brought brand-new opportunities to classic optimization field and has motivated novel breakthroughs in large-scale optimization. We focus on exploring practical optimization algorithms and toolkit which co-improve with advanced AI system.
            </p>
          </div>
        </div>
        <!-- 向下滑动提示箭头 -->
        <div class="scroll-down-indicator">
          <div class="scroll-arrow">
            <span>↓</span>
          </div>
          <div class="scroll-text">scroll down</div>
        </div>
      </section>
      <!-- 演讲者部分 -->
      <section id="speakers" class="section speakers-section">
        <div class="container">
          <div class="section-title">
            <h2>Speakers</h2>
            <!--<p>我们荣幸地邀请到了来自世界各地的知名学者和专家</p>-->
          </div>
          <div class="speakers-grid">
            <div class="speaker-card" v-for="speaker in speakers" :key="speaker.id" @click="openSpeakerLink(speaker.link)" style="cursor: pointer;">
              <div class="speaker-avatar">
                <img :src="speaker.avatar" :alt="speaker.name" />
              </div>
              <div class="speaker-info">
                <h3 class="speaker-name">
                  {{ speaker.name }}
                </h3>
                <p class="speaker-affiliation">{{ speaker.affiliation }}</p>
                <!-- <p class="speaker-title">{{ speaker.title }}</p> -->
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- 会议安排部分 -->
      <section id="schedule" class="section schedule-section">
        <div class="container">
          <div class="section-title">
            <h2>Schedule</h2>
            <p>December 7, 2025</p>
          </div>
          <div class="timeline">
            <div class="timeline-item" v-for="item in schedule" :key="item.id">
              <div class="timeline-time">{{ item.time }}</div>
              <div class="timeline-content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
                <span v-if="item.speaker" class="timeline-speaker">Speaker：{{ item.speaker }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- 投稿须知部分 -->
      <section id="submission" class="section submission-section">
        <div class="container">
          <h2 class="main-title">Call for Papers</h2>

          <div class="submission-content">
            <div class="content-grid">
              <!-- 左侧内容 -->
              <div class="left-column">
                <div class="section-block">
                  <h3>Overview</h3>
<p>
Recent advancements in GPU-based large-scale optimization have been remarkable.
Recognizing the revolution in optimizing neural network weights via large-scale GPU-accelerated algorithms, the optimization community has been interested in developing general purpose GPU-accelerated optimizers for various families of classic optimization problems, including linear programming, general conic optimization, combinatorial optimization, and more specific problem families such as flow optimization and optimal transport.
<em> This workshop welcomes submissions explaining how to deploy GPUs for optimization; relevant works could span theoretical work&mdash;designing novel highly-parallel optimization algorithms suitable for modern GPU implementation&mdash;to impressive engineering work&mdash;designing specialized implementations with custom CUDA kernels to accelerate the numerical computations.</em>
</p>
<p>
Beyond deploying GPUs directly at classical problems, current frontier AI tools&mdash;including large language models (LLMs)&mdash;are being deployed to solve optimization problem.
Specifically, researchers have used LLMs to solve linear programs and in combinatorial optimization.
Various works have used neural networks to solve mixed integer problems, linear or quadratic programs, general combinatorial optimization problems, and more specific optimization problems such as LASSO and robust PCA.
<em> This workshop welcomes works related to Learning to Optimize (L2O)&mdash;using neural networks to learn to solve classic optimization problems&mdash;and other forms of modern techniques being deployed to solve classic optimization problems. </em>
</p>
<p>
In this workshop, we aim to provide a platform for interested researchers to engage with each other on recent breakthroughs and current bottlenecks in designing large-scale GPU-based optimizers and synergizing AI systems with solving optimization problems.
</p>
                  <h3>Keywords</h3>

<p> We welcome submissions in, but not limited to, the following areas:
<ul style="margin:1em">
<li> Parallel algorithms </li>
<li> Learning to optimize (L2O)</li>
<li> Optimization software package development </li>
<li> Accelerated optimization methods </li>
<li> Advancements in GPU-based optimization </li>
<li> Large-scale distributed optimization </li>
<li> Robust and adaptive optimization </li>
<li> Randomized numerical linear algebra for optimization </li>
</ul>
</p>
                </div>
              </div>

              <!-- 右侧内容 -->
              <div class="right-column">

                <div class="section-block">
<h3>Submission Instructions</h3>
<ul style="margin:1em">
<li><strong>Submission:</strong> <a
href="https://openreview.net/group?id=NeurIPS.cc%2F2025%2FWorkshop%2FScaleOPT">OpenReview
website</a>.
<ul style="margin:1em">
<li style="margin:1em">New OpenReview profiles <strong>without an institutional
email</strong> are subject to moderation (up to two weeks).</li>
<li style="margin:1em">Profiles <strong>with an institutional email</strong> are activated
automatically.</li>
</ul></li>
<li style="margin:1em"><strong>Page limit:</strong> 6 pages (excluding references and
appendices).
<ul style="margin:1em">
<li style="margin:1em">Papers can be up to 6 pages total.</li>
</ul></li>
<li style="margin:1em"><strong>Supplementary material:</strong> Reasonable supplementary
material is allowed, but reviewers are not required to review it. The
main paper should be self-contained.</li>
<li style="margin:1em"><strong>Format:</strong> Use the <a
href="https://neurips.cc/Conferences/NeurIPS2025/Styles.zip">NeurIPS
style files</a> for both submission and camera-ready/arXiv
versions.</li>
<li style="margin:1em"><strong>Anonymity:</strong> Submissions must be anonymized for
double-blind review.</li>
<li style="margin:1em"><strong>Dual submissions:</strong>
<ul style="margin:1em">
<li style="margin:1em">Submissions accepted at other venues with archival proceedings
(including NeurIPS main conference) are <strong>not</strong>
allowed.</li>
<li style="margin:1em">Dual submissions to other NeurIPS workshops are not allowed—pick the
most appropriate workshop.</li>
<li style="margin:1em">Extended abstracts of work under review at other conferences or
journals are allowed if permitted by that venue.</li>
</ul></li>
<li style="margin:1em"><strong>Publication:</strong> Accepted papers will be posted online,
but there are <strong>no archival proceedings</strong>.</li>
</ul>
                </div>
               <div class="section-block">
                  <h3>Important Dates</h3>
                  <div class="dates-list">
                    <div class="date-item">
                      <span class="date-label">Submission Open:</span>
                      <span class="date-value">July 25, 2025</span>
                    </div>
                    <div class="date-item">
                      <span class="date-label">Deadline:</span>
                      <span class="date-value">August 22, 2025</span>
                    </div>
                    <div class="date-item">
                      <span class="date-label">Notification of Acceptance:</span>
                      <span class="date-value">September 22, 2025</span>
                    </div>
                    <div class="date-item">
                      <span class="date-label">Camera-ready Papers Upload to Openreview:</span>
                      <span class="date-value">November 21, 2025</span>
                    </div>
                    <div class="date-item">
                      <span class="date-label">Workshop Date:</span>
                      <span class="date-value">December 7, 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- 组织者部分 -->
      <section id="organizers" class="section organizers-section">
        <div class="container">
          <div class="section-title">
            <h2>Organizers</h2>
            <!--<p>致力于学术交流与发展的优秀团队</p>-->
          </div>
          <div class="organizers-grid">
            <div class="organizer-card" v-for="organizer in organizers" :key="organizer.id" @click="openOrganizerLink(organizer.url)" style="cursor: pointer;">
              <div class="organizer-avatar">
                <img :src="organizer.avatar" :alt="organizer.name" />
              </div>
              <div class="organizer-info">
                <h3 class="organizer-name">
                  {{ organizer.name }}
                </h3>
                <p class="organizer-affiliation">{{ organizer.affiliation }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- Program Committee部分 -->
      <section id="program-committee" class="section program-committee-section">
        <div class="container">
          <div class="section-title">
            <h2>Program Committee</h2>
          </div>
          <div class="committee-grid">
            <div class="committee-member">Yunke Ao (ETH)</div>
            <div class="committee-member">Amirhossein Afsharrad (Stanford)</div>
            <div class="committee-member">Quentin Bertrand (INRIA)</div>
            <div class="committee-member">Jieyi Bi (NTU)</div>
            <div class="committee-member">Louis Bouvier (Ecole des Ponts)</div>
            <div class="committee-member">Sanghyeok Choi (KAIST)</div>
            <div class="committee-member">Somnath Chowdhury (UNC)</div>
            <div class="committee-member">Jeremy Cohen (Flatiron)</div>
            <div class="committee-member">Victor-Alexandru Darvariu (Oxford University)</div>
            <div class="committee-member">Frank DeGuire (Stanford)</div>
            <div class="committee-member">Francesco Demelas (USPN)</div>
            <div class="committee-member">Darko Drakulic (NAVER LABS)</div>
            <div class="committee-member">Zachary Frangella (Stanford)</div>
            <div class="committee-member">Wenzhi Gao (Stanford)</div>
            <div class="committee-member">Zijie Geng (USTC)</div>
            <div class="committee-member">Paul Häusner (Uppsala University)</div>
            <div class="committee-member">Yutong He (PKU)</div>
            <div class="committee-member">Yifan Hu (EPFL)</div>
            <div class="committee-member">Bingheng Li (MSU)</div>
            <div class="committee-member">Ling Liang (University of Maryland)</div>
            <div class="committee-member">Feng-Yi Liao (UCSD)</div>
            <div class="committee-member">Jonathan Lorraine (NVIDIA)</div>
            <div class="committee-member">Jianhao Ma (UMich)</div>
            <div class="committee-member">Thomas Moreau (INRIA)</div>
            <div class="committee-member">Chih-Fan Pai (UCSD)</div>
            <div class="committee-member">Jianming Pan (UC Berkeley)</div>
            <div class="committee-member">Jisun Park (Princeton)</div>
            <div class="committee-member">Ruizhong Qiu (UIUC)</div>
            <div class="committee-member">Zhongdi Qu (Cornell)</div>
            <div class="committee-member">Pratik Rathore (Stanford)</div>
            <div class="committee-member">Syed Rizvi (Yale)</div>
            <div class="committee-member">Rajiv Sambharya (UPenn)</div>
            <div class="committee-member">Xu Shang (UCSD)</div>
            <div class="committee-member">Emi Soroka (Stanford)</div>
            <div class="committee-member">Jaewook Suh (Rice)</div>
            <div class="committee-member">Hong Ye Tan (University of Cambridge)</div>
            <div class="committee-member">Bo Tang (University of Toronto)</div>
            <div class="committee-member">Tianyun Tang (NUS)</div>
            <div class="committee-member">Zhihai Wang (USTC)</div>
            <div class="committee-member">Jianan Zhou (NTU)</div>
          </div>
        </div>
      </section>

      <!-- 联系方式部分 -->
      <section class="contact-section">
        <div class="container">
          <!-- 组织原则模块 -->
          <div class="organizing-principles">
            <h3>Organizing Principles </h3>
            <div class="principles-content">
              <p>Our workshop is committed to recognizing that optimization is, and should become, a welcoming community. We welcome participation by people of all ages, seniorities, races, religions, creeds, colors, genders, sexual orientations, gender expressions, medical conditions, disabilities, national origins, and ancestries.</p>

              <p>To pursue accessibility, we look forward to providing any needed accommodations for participants with disabilities, permanent or temporary. If you would benefit from accommodations, please contact our accommodations coordinator, Parth Nobel, to discuss any accessibility needs.</p>
<p>To facilitate the diversity of attendance across seniority and means, we welcome interested participants who would not otherwise be able to attend NeurIPS to apply for our free registrations. <a href="https://forms.gle/Cy5kwj4z3fZNpyhx8">Please fill out the form here by October 5th</a>.</p>
            </div>
          </div>

          <!-- 校徽展示 -->
          <div class="university-logos">
            <div class="logo-item">
              <img src="/scaleopt/static/logo/logo1.png" alt="清华大学" class="university-logo">
            </div>
            <div class="logo-item">
              <img src="/scaleopt/static/logo/logo2.jpg" alt="北京大学" class="university-logo">
            </div>
            <div class="logo-item">
              <img src="/scaleopt/static/logo/logo4.png" alt="上海交通大学" class="university-logo">
            </div>
            <div class="logo-item">
              <img src="/scaleopt/static/logo/logo2.png" alt="上海交通大学" class="university-logo">
            </div>
          </div>
          <!-- 联系信息 -->
          <div class="contact-footnote">
            <p>Contact: scaleopt@googlegroups.com</p>
          </div>
        </div>
      </section>
    </main>
    <!-- 页脚 -->
    <footer class="footer">
      <div class="container">
        <p>&copy; ScaleOPT Workshop 2025</p>
      </div>
    </footer>
  </div>
</template>
<script>
export default {
  name: 'App',
  data() {
    return {
      speakers: [
        {
          id: 1,
          name: 'Wotao Yin',
          affiliation: 'Alibaba',
          title: '计算机科学系教授',
          avatar: '/scaleopt/static/Speakers/Wotao-Yin.png',
          link: 'https://wotaoyin.mathopt.com/'
        },
        {
          id: 2,
          name: 'Pratik Rathore',
          affiliation: 'Stanford',
          title: '人工智能研究主任',
          avatar: '/scaleopt/static/Speakers/Pratik-Rathore.png',
          link: 'https://pratikrathore8.github.io/'
        },
        {
          id: 3,
          name: 'Haihao Lu',
          affiliation: 'MIT',
          title: '医疗AI研究总监',
          avatar: '/scaleopt/static/Speakers/Haihao-Lu.jpeg',
          link: 'https://mitsloan.mit.edu/faculty/directory/haihao-lu/'
        },
        {
          id: 4,
          name: 'Brandon Amos',
          affiliation: 'FAIR',
          title: '机器学习教授',
          avatar: '/scaleopt/static/Speakers/Brandon.jpeg',
          link: 'https://bamos.github.io/'
        },
        {
          id: 5,
          name: 'Bartolomeo Stellato',
          affiliation: 'Princeton',
          title: '机器学习系副教授',
          avatar: '/scaleopt/static/Speakers/Bart.jpeg',
          link: 'https://stella.to/'
        },
        {
          id: 6,
          name: 'Danny Tse',
          affiliation: 'Stanford',
          title: '健康AI产品总监',
          avatar: '/scaleopt/static/Speakers/Danny.jpeg',
          link: 'https://www.linkedin.com/in/danny-tse/'
        }
      ],
      schedule: [
        {
          id: 1,
          time: '8:45AM—9:00AM',
          title: 'Opening Remarks',
          description: '',
          speaker: 'Organizers'
        },
        {
          id: 2,
          time: '9:00AM—9:30AM',
          title: 'GPU-enabled large-scale optimization using randomized linear algebra',
          description: 'Despite the growing importance of optimization, existing solvers suffer from several key drawbacks: (i) they often cannot scale to large-scale optimization problems due to high computational/memory costs (e.g., expensive matrix factorizations), (i) they are unable to take advantage of modern parallel hardware (i.e., limited to CPU), and (iii) they do not provide a simple, user-friendly syntax for modeling modern optimization problems. To address these issues, we develop rlaopt, a PyTorch-based package containing high-quality, GPU-enabled methods for large-scale optimization and scientific computing. rlaopt is easy to use: we create a flexible modeling language that lets users specify problems via natural mathematical syntax and demonstrate its simplicity for formulating and solving large-scale linear systems, empirical risk minimization, and more. Randomized numerical linear algebra is central to our approach, as it mitigates ill-conditioning for linear systems and unlocks scalability in stochastic optimization methods and operator splitting frameworks. Moreover, rlaopt allows for differentiating through the solver, making it suitable for modern machine learning pipelines.',
          speaker: 'Pratik Rathore'
        },
        {
          id: 3,
          time: '9:30AM—10:00AM',
          title: 'GPU-accelerated Quadratic Conic Solvers',
          description: 'We introduce CuClarabel, a GPU-accelerated version of the interior-point solver Clarabel for quadratic cone programs. We describe the implementation through overviewing the homogeneous embedding method used in Clarabel and the mixed parallel computing strategy in CuClarabel. Compared to its CPU counterpart and other CPU-based solvers, the GPU solver demonstrates several times superior performance on various classes of problems, without compromising precision. Next, we survey backpropagation through convex problems leveraging differentiable cone programs, and showcase differentiating through quadratic cone programs. Through a blend of this work, we outline a forward path to efficient, GPU-accelerated differentiable convex optimization layers.',
          speaker: 'Danny Tse'
        },
        {
          id: 4,
          time: '10:00AM—10:30AM',
          title: 'An Overview of GPU-Based First-Order Methods for Linear Programming and Beyond',
          description: 'The rapid advancement of GPU computing has transformed numerous fields, yet its impact on mathematical programming, including linear programming (LP) and beyond, remains an actively evolving research area. In this lecture, we aim to provide an overview of recent developments in GPU-accelerated solvers, with a primary focus on first-order methods (FOMs) for LP and their extensions. We begin by outlining the fundamental differences between CPU and GPU architectures and presenting a numerical study on the speedup achieved by GPUs over CPUs for key numerical linear algebra operations that serve as bottlenecks for various optimization algorithms. Next, we discuss how to design FOMs for problems like LP from the ground up. We then introduce the newly developed LP solvers PDLP, cuPDLP, and cuPDLPx, describing practical enhancements that improve numerical performance, along with a summary of numerical studies comparing cuPDLPx to commercial solvers. This is followed by an in-depth discussion of the theoretical foundations of the primal-dual hybrid gradient (PDHG) method, which serves as the base algorithm for PDLP. We present a simplified and unified perspective on PDHG, including a basic convergence analysis, and explore its connections with non-expansive operators, leading to a principal understanding of the algorithm. Furthermore, we examine the sharpness property of LP, a key characteristic that enables the linear convergence of PDHG. We discuss optimal linear convergence, infeasibility detection, and Halpern acceleration, followed by a summary of condition-number-based analyses. Finally, we review recent progress in GPU-based solvers for quadratic and semidefinite programming, highlighting broader advancements in large-scale optimization with GPUs.',
          speaker: 'Haihao Lu'
        },
        {
          id: 5,
          time: '10:30AM—11:00AM',
          title: 'Breakout: Algorithmic Opportunities for GPU and CPU Unified Memory',
          description: 'During breakout sessions, workshop participants will be invited to discuss the key scientific challenges underlying the topic.'
        },
        {
          id: 6,
          time: '11:00AM—11:15AM',
          title: 'GPU Implementation of Second-Order Linear and Nonlinear Programming Solvers',
          description: 'In recent years, GPU-accelerated optimization solvers based on second-order methods (e.g., interior-point methods) have gained momentum with the advent of mature and efficient GPU-accelerated direct sparse linear solvers, such as cuDSS. This paper provides an overview of the state of the art in GPU-based second-order solvers, focusing on pivoting-free interior-point methods for large and sparse linear and nonlinear programs. We begin by highlighting the capabilities and limitations of the currently available GPU-accelerated sparse linear solvers. Next, we discuss different formulations of the Karush-Kuhn-Tucker systems for second-order methods and evaluate their suitability for pivoting-free GPU implementations. We also discuss strategies for computing sparse Jacobians and Hessians on GPUs for nonlinear programming. Finally, we present numerical experiments demonstrating the scalability of GPU-based optimization solvers. We observe speedups often exceeding 10× compared to comparable CPU implementations on large-scale instances when solved up to medium precision. Additionally, we examine the current limitations of existing approaches.',
          speaker: 'Sungho Shin'
        },
        {
          id: 7,
          time: '11:15AM—11:30AM',
          title: 'Learning-based hierarchical approach for mixed integer optimization',
          description: 'We propose a hierarchical architecture for efficiently computing high-quality solutions to structured mixed-integer programs (MIPs). To reduce computational effort, our approach decouples the original problem into a higher level problem and a lower level problem, both of smaller size. We solve both problems sequentially, where decisions of the higher level problem become parameters of the constraints of the lower level problem. To overcome potential suboptimalities introduced by the hierarchical decomposition, we learn a predictor that outputs the objective coefficients of the higher level problem, in order to maximize the solution quality of the overall architecture. We formulate this learning task as a convex optimization problem with using decision-focused learning techniques and solve it by differentiating through the higher and the lower level problems in our architecture. To ensure robustness, we derive out-of-sample performance guarantees using conformal prediction. Numerical experiments in facility location, knapsack problems, and vehicle routing problems demonstrate that our approach significantly reduces computation time while maintaining feasibility and high solution quality compared to state-of-the-art solvers',
          speaker: 'Stefan Clarke'
        },
        {
          id: 8,
          time: '11:30AM—12:30PM',
          title: 'Poster Session 1',
          description: ''
        },
        {
          id: 9,
          time: '12:30PM—1:30PM',
          title: 'Lunch Break',
          description: ''
        },
        {
          id: 10,
          time: '1:30PM—1:45PM',
          title: 'Nonlinear Optimization with GPU-Accelerated Neural Network Constraints',
          description: 'We propose a reduced-space formulation for optimizing over trained neural networks where the network\'s outputs and derivatives are evaluated on a GPU. To do this, we treat the neural network as a "gray box" where intermediate variables and constraints are not exposed to the optimization solver. Compared to the full-space formulation, in which intermediate variables and constraints are exposed to the optimization solver, the reduced-space formulation leads to faster solves and fewer iterations in an interior point method. We demonstrate the benefits of this method on two optimization problems: Adversarial generation for a classifier trained on MNIST images and security-constrained optimal power flow with transient feasibility enforced using a neural network surrogate.',
          speaker: 'Robert Parker'
        },
        {
          id: 11,
          time: '1:45PM—2:00PM',
          title: 'Learning to optimize over linearly convergent algorithms: gotta characterize \'em all',
          description: 'In high-stakes engineering applications, optimizers must come with provable worst-case guarantees over a mathematically defined class of problems. Yet, designing for the worst case often sacrifices performance on the instances that arise in practice. While learning to optimize approaches hold promise in enhancing average-case optimizer performance on sets of target problems of interest, they often require conservative safe-guarding mechanisms in order to maintain convergent behavior. In this paper, we characterize the class of algorithms that achieve linear convergence for several classes of nonsmooth composite optimization problems. This is achieved by parametrizing all augmentations to a baseline linearly convergent algorithm – that is, all perturbations to its update rule – that preserve its certified rate on the entire class. Our results apply to augmenting algorithms such as gradient descent for nonconvex, gradient-dominated functions; Nesterov’s accelerated method for strongly convex functions; and projected methods for optimization over polyhedral feasibility sets. We showcase effectiveness of the approach on solving optimization problems with tight iteration budgets in application to ill-conditioned systems of linear equations and model predictive control for linear systems.',
          speaker: 'Luca Furieri'
        },
        {
          id: 12,
          time: '2:00PM—2:30PM',
          title: 'Breakout: Finetuning LLMs for Optimization',
          description: 'During breakout sessions, workshop participants will be invited to discuss the key scientific challenges underlying the topic.'
        },
        {
          id: 13,
          time: '2:30PM—3:00PM',
          title: 'On meta prompt optimization and coding agents',
          description: 'Language models (LMs) encode vast amounts of human knowledge in a queryable form and are now widely deployed across domains that previously could not directly benefit from language. Yet major challenges remain: how to effectively extract information (e.g., where and how to prompt the model), and how to ensure alignment with human values and preferences (e.g., personalization, safety, undesirable behaviors). A path forward to solving these is to quantify the objective, perform (test-time) optimization over the prompt space to improve that objective, and ultimately integrate the improvement back into the base model. This talk explores this in two domains: 1. Prompt optimization and adversarial attacks — in AdvPrompter (ICML 2025) we learn a /meta-prompting/ model that rapidly predicts the optimal adversarial suffix for jailbreaking an LM, and in AdvPrefix (NeurIPS 2025), we show variants on the jailbreaking optimization problem objective can result in significantly improved performance, and 2. Numerical code synthesis with agents — in AlgoTune (NeurIPS D&B 2025), we propose a benchmark suite of 154 numerical coding tasks along with a baseline code agent to solve it. The coding agent iteratively optimizes the code (and prompts) for the task’s runtime as the objective.  We’ll draw connections between these two seemingly separate settings and show how meta-learning and agentic test-time computations squeeze the most out of the base model, advancing both capabilities and alignment.',
          speaker: 'Brandon Amos'
        },
        {
          id: 14,
          time: '3:00PM—3:30PM',
          title: 'Accelerating Parametric Convex Optimization: Design and Verification of First-Order Methods',
          description: 'We present statistical learning tools to analyze and design accelerated first-order methods for parametric convex optimization. We establish generalization guarantees for classical optimizers through sample convergence bounds and for learned optimizers using the Probably Approximately Correct (PAC)-Bayes framework. These guarantees depend on the iterate trajectories that we collect by modeling the iterations as computational graphs and running evaluations in parallel on GPUs. Building on this, we design accelerated first-order methods by tuning key algorithm parameters (such as gradient steps and warm-starts) with provable performance bounds that closely match their out-of-sample behavior. Together, these results provide a pathway toward automated algorithm design and verification, bringing together theoretical insights with empirical performance.',
          speaker: 'Bartolomeo Stellato'
        },
        {
          id: 15,
          time: '3:30PM—4:00PM',
          title: 'Automation in Optimization: Enhancing Decomposition for Proximal and CPU/GPU-Parallel Methods',
          description: 'Optimization practitioners encounter substantial hurdles when transforming and decomposing optimization problems, mainly when these problems comprise diverse components. Some elements may be ideally suited for proximal operators, which excel at managing non-smooth or constrained functions, while others lend themselves to parallel computing, enabling faster computation through distributed workloads. Practitioners must manually determine how to integrate these approaches effectively, demanding deep expertise and considerable time. An automated process could transform this landscape by analyzing problem structures and seamlessly applying the most appropriate techniques to each component. In addition, automation could harness recent advancements in automated parameter selection and acceleration techniques for first-order algorithms, which enhance convergence speed and performance without manual tuning. We introduce an automated system that optimizes computational resources and delivers high-performance solutions, allowing experts to concentrate on strategic tasks like problem formulation and result interpretation.',
          speaker: 'Wotao Yin'
        },
        {
          id: 16,
          time: '4:00PM—5:00PM',
          title: 'Poster Session 2',
          description: ''
        }
      ],
      organizers: [
        {
          id: 1,
          name: 'Parth Nobel',
          affiliation: 'Stanford',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Parth.jpeg',
          url: 'https://ptnobel.github.io/'
        },
        {
          id: 2,
          name: 'Fangzhao Zhang',
          affiliation: 'Stanford',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Fangzhao.jpg',
          url: 'https://fangzhaoz.github.io/'
        },
        {
          id: 3,
          name: 'Maximilian Schaller',
          affiliation: 'Stanford',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Max3.jpeg',
          url: 'https://maxschaller.github.io/'
        },
        {
          id: 4,
          name: 'Alexandre Amice',
          affiliation: 'MIT',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Alex.jpg',
          url: 'https://alexandreamice.github.io/'
        },
        {
          id: 5,
          name: 'Tobia Marcucci',
          affiliation: 'UCSB',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Tobia.png',
          url: 'https://tobiamarcucci.github.io/'
        },
        {
          id: 6,
          name: 'Tetiana Parshakova',
          affiliation: 'Flatiron Institute',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Tanya.jpg',
          url: 'https://parshakova.github.io/'
        },
        {
          id: 7,
          name: 'Stephen Boyd',
          affiliation: 'Stanford',
          role: '大会主席',
          avatar: '/scaleopt/static/organizers/Stephen.jpg',
          url: 'https://web.stanford.edu/~boyd'
        }
      ],
      personnel: [
        {
          id: 1,
          name: '张三',
          university: 'Tsinghua University'
        },
        {
          id: 2,
          name: '李四',
          university: 'Peking University'
        },
        {
          id: 3,
          name: '王五',
          university: 'Massachusetts Institute of Technology'
        },
        {
          id: 4,
          name: '赵六',
          university: 'Shanghai Jiao Tong University'
        },
        {
          id: 5,
          name: '孙七',
          university: 'University of California, Berkeley'
        },
        {
          id: 6,
          name: '周八',
          university: 'Stanford University'
        }
      ]
    }
  },
  methods: {
    // 打开speaker链接
    openSpeakerLink(link) {
      window.open(link, '_blank')
    },
    // 打开organizer链接
    openOrganizerLink(url) {
      window.open(url, '_blank')
    },
    // 星星交互
    lightUpStar(event) {
      const star = event.currentTarget
      star.style.transform = 'scale(1.5) rotate(180deg)'
      star.style.color = '#ffff00'
      star.style.textShadow = '0 0 20px #ffff00, 0 0 40px #ffff00'
    },
    dimStar(event) {
      const star = event.currentTarget
      star.style.transform = 'scale(1) rotate(0deg)'
      star.style.color = 'rgba(255, 255, 255, 0.6)'
      star.style.textShadow = '0 0 10px rgba(255, 255, 255, 0.6)'
    }
  },
  mounted() {
    // 平滑滚动到锚点
    document.querySelectorAll('.nav-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const targetId = link.getAttribute('href').substring(1)
        const targetElement = document.getElementById(targetId)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' })
        }
      })
    })

    // 初始化粒子连线特效
    this.initParticleEffect()
  },
  beforeDestroy() {
    // 清理粒子特效
    if (this.$particleCleanup) {
      this.$particleCleanup()
    }
  }
}
</script>
<style>
/* 导入现代化字体 */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
:root {
  /* 精致的配色方�?*/
  --primary-color: #667eea;
  --primary-dark: #5a6fd8;
  --primary-light: #9f7aea;
  --secondary-color: #764ba2;
  --accent-color: #f093fb;
  --success-color: #4facfe;
  --warning-color: #f6d365;
  --error-color: #ffecd2;
  /* 现代中性色 */
  --gray-50: #fafafa;
  --gray-100: #f4f4f5;
  --gray-200: #e4e4e7;
  --gray-300: #d4d4d8;
  --gray-400: #a1a1aa;
  --gray-500: #71717a;
  --gray-600: #52525b;
  --gray-700: #3f3f46;
  --gray-800: #27272a;
  --gray-900: #18181b;
  /* 精致渐变 */
  --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --gradient-secondary: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  --gradient-accent: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  --gradient-warm: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  --gradient-cool: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  /* 精致阴影系统 */
  --shadow-elegant: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-soft: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-medium: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-large: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --shadow-glow: 0 0 20px rgba(102, 126, 234, 0.4);
  /* 现代字体 */
  --font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
body {
  font-family: var(--font-family);
  line-height: 1.6;
  color: var(--gray-800);
  background: url('/scaleopt/static/bg.jpg') center center no-repeat;
  background-size: cover;
  background-attachment: fixed;
  font-size: 16px;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
  overflow-x: hidden;
}
/* 增强科技感覆盖层 */
body::after {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
  z-index: 1;
}
/* 全局玻璃态覆盖层 */
body::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  z-index: -1;
  pointer-events: none;
}
#app {
  min-height: 100vh;
  position: relative;
  z-index: 1;
}
/* 全局动态背景效�?*/
.global-background-effects {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 5;
}
/* 浮动光球效果 - 模拟网络节点 */
.floating-orbs {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.floating-orb {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(0, 162, 255, 0.6) 40%, rgba(0, 162, 255, 0) 70%);
  filter: blur(1px);
  animation: float-network-node linear infinite;
  box-shadow: 0 0 20px rgba(0, 162, 255, 0.4);
}
.floating-orb:nth-child(1) {
  width: 8px;
  height: 8px;
  top: 20%;
  left: 10%;
  animation-duration: 12s;
  animation-delay: 0s;
  background: radial-gradient(circle, rgba(0, 162, 255, 0.9) 0%, rgba(0, 162, 255, 0.3) 40%, rgba(0, 162, 255, 0) 70%);
  box-shadow: 0 0 15px rgba(0, 162, 255, 0.6);
}
.floating-orb:nth-child(2) {
  width: 6px;
  height: 6px;
  top: 50%;
  left: 80%;
  animation-duration: 15s;
  animation-delay: 2s;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.2) 40%, rgba(255, 255, 255, 0) 70%);
}
.floating-orb:nth-child(3) {
  width: 10px;
  height: 10px;
  top: 70%;
  left: 20%;
  animation-duration: 18s;
  animation-delay: 4s;
  background: radial-gradient(circle, rgba(255, 165, 0, 0.8) 0%, rgba(255, 165, 0, 0.3) 40%, rgba(255, 165, 0, 0) 70%);
  box-shadow: 0 0 18px rgba(255, 165, 0, 0.5);
}
.floating-orb:nth-child(4) {
  width: 4px;
  height: 4px;
  top: 30%;
  left: 60%;
  animation-duration: 10s;
  animation-delay: 1s;
}
.floating-orb:nth-child(5) {
  width: 12px;
  height: 12px;
  top: 10%;
  left: 90%;
  animation-duration: 14s;
  animation-delay: 3s;
  background: radial-gradient(circle, rgba(255, 105, 180, 0.8) 0%, rgba(255, 105, 180, 0.3) 40%, rgba(255, 105, 180, 0) 70%);
  box-shadow: 0 0 20px rgba(255, 105, 180, 0.4);
}
.floating-orb:nth-child(6) {
  width: 7px;
  height: 7px;
  top: 80%;
  left: 70%;
  animation-duration: 16s;
  animation-delay: 5s;
}
.floating-orb:nth-child(7) {
  width: 5px;
  height: 5px;
  top: 40%;
  left: 5%;
  animation-duration: 11s;
  animation-delay: 2.5s;
}
.floating-orb:nth-child(8) {
  width: 9px;
  height: 9px;
  top: 60%;
  left: 45%;
  animation-duration: 13s;
  animation-delay: 4.5s;
  background: radial-gradient(circle, rgba(144, 238, 144, 0.7) 0%, rgba(144, 238, 144, 0.2) 40%, rgba(144, 238, 144, 0) 70%);
  box-shadow: 0 0 16px rgba(144, 238, 144, 0.5);
}
.floating-orb:nth-child(9) {
  width: 6px;
  height: 6px;
  top: 15%;
  left: 40%;
  animation-duration: 17s;
  animation-delay: 1.5s;
}
.floating-orb:nth-child(10) {
  width: 8px;
  height: 8px;
  top: 85%;
  left: 85%;
  animation-duration: 19s;
  animation-delay: 3.5s;
}
.floating-orb:nth-child(11) {
  width: 3px;
  height: 3px;
  top: 25%;
  left: 25%;
  animation-duration: 20s;
  animation-delay: 0.5s;
}
.floating-orb:nth-child(12) {
  width: 11px;
  height: 11px;
  top: 55%;
  left: 95%;
  animation-duration: 8s;
  animation-delay: 2.8s;
  background: radial-gradient(circle, rgba(64, 224, 208, 0.8) 0%, rgba(64, 224, 208, 0.3) 40%, rgba(64, 224, 208, 0) 70%);
  box-shadow: 0 0 22px rgba(64, 224, 208, 0.4);
}
.floating-orb:nth-child(13) {
  width: 5px;
  height: 5px;
  top: 33%;
  left: 12%;
  animation-duration: 15s;
  animation-delay: 1.8s;
}
.floating-orb:nth-child(14) {
  width: 7px;
  height: 7px;
  top: 77%;
  left: 52%;
  animation-duration: 21s;
  animation-delay: 4.2s;
}
.floating-orb:nth-child(15) {
  width: 4px;
  height: 4px;
  top: 8%;
  left: 68%;
  animation-duration: 22s;
  animation-delay: 6.5s;
}
@keyframes float-network-node {
  0%, 100% {
    transform: translate(0px, 0px) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(20px, -15px) scale(1.3);
    opacity: 0.8;
  }
  50% {
    transform: translate(-10px, -30px) scale(0.8);
    opacity: 0.6;
  }
  75% {
    transform: translate(15px, -20px) scale(1.1);
    opacity: 0.9;
  }
}
/* 极光带效�?*/
.aurora-lights {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.aurora-band {
  position: absolute;
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg,
    rgba(0, 255, 150, 0) 0%,
    rgba(0, 255, 150, 0.3) 25%,
    rgba(100, 200, 255, 0.4) 50%,
    rgba(255, 100, 200, 0.3) 75%,
    rgba(255, 255, 100, 0) 100%);
  filter: blur(3px);
  animation: aurora-wave linear infinite;
  transform-origin: center;
}
.aurora-band:nth-child(1) {
  top: 10%;
  animation-duration: 20s;
  animation-delay: 0s;
  opacity: 0.15;
}
.aurora-band:nth-child(2) {
  top: 40%;
  animation-duration: 25s;
  animation-delay: 5s;
  opacity: 0.12;
  background: linear-gradient(90deg,
    rgba(255, 100, 150, 0) 0%,
    rgba(255, 100, 150, 0.3) 25%,
    rgba(150, 100, 255, 0.4) 50%,
    rgba(100, 255, 200, 0.3) 75%,
    rgba(255, 200, 100, 0) 100%);
}
.aurora-band:nth-child(3) {
  top: 70%;
  animation-duration: 30s;
  animation-delay: 10s;
  opacity: 0.13;
  background: linear-gradient(90deg,
    rgba(100, 255, 255, 0) 0%,
    rgba(100, 255, 255, 0.3) 25%,
    rgba(255, 150, 100, 0.4) 50%,
    rgba(150, 255, 150, 0.3) 75%,
    rgba(255, 100, 255, 0) 100%);
}
.aurora-band:nth-child(4) {
  top: 25%;
  animation-duration: 22s;
  animation-delay: 15s;
  opacity: 0.1;
  height: 150px;
}
.aurora-band:nth-child(5) {
  top: 55%;
  animation-duration: 28s;
  animation-delay: 20s;
  opacity: 0.08;
  height: 180px;
  background: linear-gradient(90deg,
    rgba(255, 215, 0, 0) 0%,
    rgba(255, 215, 0, 0.2) 25%,
    rgba(64, 224, 208, 0.3) 50%,
    rgba(255, 105, 180, 0.2) 75%,
    rgba(173, 216, 230, 0) 100%);
}
.aurora-band:nth-child(6) {
  top: 85%;
  animation-duration: 26s;
  animation-delay: 12s;
  opacity: 0.09;
  height: 120px;
  background: linear-gradient(90deg,
    rgba(147, 112, 219, 0) 0%,
    rgba(147, 112, 219, 0.2) 25%,
    rgba(255, 182, 193, 0.3) 50%,
    rgba(176, 224, 230, 0.2) 75%,
    rgba(240, 230, 140, 0) 100%);
}
@keyframes aurora-wave {
  0% {
    transform: translateX(-100%) skewX(-5deg);
  }
  100% {
    transform: translateX(100%) skewX(5deg);
  }
}
/* 几何图形动画 */
.geometric-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.geo-shape {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: geo-morph linear infinite;
}
.geo-shape:nth-child(1) {
  width: 80px;
  height: 80px;
  top: 15%;
  left: 15%;
  border-radius: 50%;
  animation-duration: 12s;
  animation-delay: 0s;
  border-color: rgba(173, 216, 230, 0.4);
}
.geo-shape:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 45%;
  left: 75%;
  animation-duration: 15s;
  animation-delay: 3s;
  border-color: rgba(255, 192, 203, 0.4);
}
.geo-shape:nth-child(3) {
  width: 100px;
  height: 100px;
  top: 75%;
  left: 35%;
  border-radius: 20px;
  animation-duration: 18s;
  animation-delay: 6s;
  border-color: rgba(144, 238, 144, 0.4);
}
.geo-shape:nth-child(4) {
  width: 40px;
  height: 40px;
  top: 35%;
  left: 55%;
  border-radius: 50%;
  animation-duration: 10s;
  animation-delay: 2s;
}
.geo-shape:nth-child(5) {
  width: 120px;
  height: 120px;
  top: 5%;
  left: 85%;
  border-radius: 30px;
  animation-duration: 20s;
  animation-delay: 8s;
  border-color: rgba(255, 165, 0, 0.3);
}
.geo-shape:nth-child(6) {
  width: 70px;
  height: 70px;
  top: 65%;
  left: 10%;
  animation-duration: 14s;
  animation-delay: 4s;
  border-color: rgba(138, 43, 226, 0.4);
}
.geo-shape:nth-child(7) {
  width: 50px;
  height: 50px;
  top: 25%;
  left: 90%;
  border-radius: 50%;
  animation-duration: 16s;
  animation-delay: 7s;
}
.geo-shape:nth-child(8) {
  width: 90px;
  height: 90px;
  top: 85%;
  left: 65%;
  border-radius: 15px;
  animation-duration: 22s;
  animation-delay: 10s;
  border-color: rgba(255, 20, 147, 0.3);
}
.geo-shape:nth-child(9) {
  width: 35px;
  height: 35px;
  top: 12%;
  left: 48%;
  border-radius: 50%;
  animation-duration: 13s;
  animation-delay: 5s;
  border-color: rgba(255, 215, 0, 0.25);
}
.geo-shape:nth-child(10) {
  width: 85px;
  height: 85px;
  top: 52%;
  left: 82%;
  border-radius: 25px;
  animation-duration: 19s;
  animation-delay: 8s;
  border-color: rgba(64, 224, 208, 0.3);
}
.geo-shape:nth-child(11) {
  width: 45px;
  height: 45px;
  top: 38%;
  left: 28%;
  animation-duration: 15s;
  animation-delay: 3s;
  border-color: rgba(255, 105, 180, 0.25);
}
.geo-shape:nth-child(12) {
  width: 110px;
  height: 110px;
  top: 72%;
  left: 8%;
  border-radius: 40px;
  animation-duration: 21s;
  animation-delay: 11s;
  border-color: rgba(147, 112, 219, 0.3);
}
@keyframes geo-morph {
  0%, 100% {
    transform: rotate(0deg) scale(1);
    opacity: 0.1;
  }
  25% {
    transform: rotate(90deg) scale(1.2);
    opacity: 0.2;
  }
  50% {
    transform: rotate(180deg) scale(0.8);
    opacity: 0.15;
  }
  75% {
    transform: rotate(270deg) scale(1.1);
    opacity: 0.18;
  }
}
/* 流星效果 */
.shooting-stars {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.shooting-star {
  position: absolute;
  width: 2px;
  height: 60px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
  border-radius: 50%;
  animation: shooting linear infinite;
}
.shooting-star:nth-child(1) {
  top: 15%;
  left: 20%;
  animation-duration: 3s;
  animation-delay: 0s;
  transform: rotate(45deg);
  background: linear-gradient(to bottom, rgba(173, 216, 230, 0.6) 0%, rgba(173, 216, 230, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(2) {
  top: 30%;
  left: 70%;
  animation-duration: 4s;
  animation-delay: 1.5s;
  transform: rotate(-45deg);
  background: linear-gradient(to bottom, rgba(255, 192, 203, 0.6) 0%, rgba(255, 192, 203, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(3) {
  top: 60%;
  left: 10%;
  animation-duration: 3.5s;
  animation-delay: 3s;
  transform: rotate(30deg);
  background: linear-gradient(to bottom, rgba(144, 238, 144, 0.6) 0%, rgba(144, 238, 144, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(4) {
  top: 80%;
  left: 60%;
  animation-duration: 4.5s;
  animation-delay: 2s;
  transform: rotate(-30deg);
  background: linear-gradient(to bottom, rgba(255, 215, 0, 0.6) 0%, rgba(255, 215, 0, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(5) {
  top: 25%;
  left: 85%;
  animation-duration: 3.8s;
  animation-delay: 4s;
  transform: rotate(60deg);
}
.shooting-star:nth-child(6) {
  top: 45%;
  left: 35%;
  animation-duration: 4.2s;
  animation-delay: 0.8s;
  transform: rotate(-60deg);
  background: linear-gradient(to bottom, rgba(255, 105, 180, 0.6) 0%, rgba(255, 105, 180, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(7) {
  top: 5%;
  left: 50%;
  animation-duration: 3.2s;
  animation-delay: 2.5s;
  transform: rotate(15deg);
  background: linear-gradient(to bottom, rgba(64, 224, 208, 0.6) 0%, rgba(64, 224, 208, 0.3) 50%, transparent 100%);
}
.shooting-star:nth-child(8) {
  top: 75%;
  left: 80%;
  animation-duration: 4.8s;
  animation-delay: 1.2s;
  transform: rotate(-15deg);
  background: linear-gradient(to bottom, rgba(147, 112, 219, 0.6) 0%, rgba(147, 112, 219, 0.3) 50%, transparent 100%);
}
@keyframes shooting {
  0% {
    opacity: 0;
    transform: translateX(-100px) translateY(-100px) rotate(var(--rotation, 45deg));
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateX(100vw) translateY(100vh) rotate(var(--rotation, 45deg));
  }
}
/* 呼吸光环效果 */
.breathing-rings {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.breathing-ring {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: ring-breath linear infinite;
}
.breathing-ring:nth-child(1) {
  width: 150px;
  height: 150px;
  top: 20%;
  left: 25%;
  animation-duration: 6s;
  animation-delay: 0s;
  border-color: rgba(173, 216, 230, 0.15);
}
.breathing-ring:nth-child(2) {
  width: 200px;
  height: 200px;
  top: 60%;
  left: 70%;
  animation-duration: 8s;
  animation-delay: 2s;
  border-color: rgba(255, 192, 203, 0.12);
}
.breathing-ring:nth-child(3) {
  width: 120px;
  height: 120px;
  top: 40%;
  left: 15%;
  animation-duration: 7s;
  animation-delay: 4s;
  border-color: rgba(144, 238, 144, 0.13);
}
.breathing-ring:nth-child(4) {
  width: 180px;
  height: 180px;
  top: 15%;
  left: 80%;
  animation-duration: 9s;
  animation-delay: 1s;
  border-color: rgba(255, 215, 0, 0.1);
}
.breathing-ring:nth-child(5) {
  width: 100px;
  height: 100px;
  top: 75%;
  left: 40%;
  animation-duration: 5s;
  animation-delay: 3s;
  border-color: rgba(255, 105, 180, 0.12);
}
.breathing-ring:nth-child(6) {
  width: 220px;
  height: 220px;
  top: 50%;
  left: 50%;
  animation-duration: 10s;
  animation-delay: 5s;
  border-color: rgba(147, 112, 219, 0.08);
}
@keyframes ring-breath {
  0%, 100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.3;
  }
}
/* 移动粒子效果 */
.moving-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.particle {
  position: absolute;
  width: 3px;
  height: 3px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: particle-float linear infinite;
}
.particle:nth-child(odd) {
  animation-name: particle-float-left;
}
.particle:nth-child(even) {
  animation-name: particle-float-right;
}
.particle:nth-child(1) { top: 10%; left: 5%; animation-duration: 15s; animation-delay: 0s; background: rgba(173, 216, 230, 0.4); }
.particle:nth-child(2) { top: 20%; left: 15%; animation-duration: 18s; animation-delay: 2s; background: rgba(255, 192, 203, 0.4); }
.particle:nth-child(3) { top: 30%; left: 25%; animation-duration: 12s; animation-delay: 4s; background: rgba(144, 238, 144, 0.4); }
.particle:nth-child(4) { top: 40%; left: 35%; animation-duration: 20s; animation-delay: 1s; background: rgba(255, 215, 0, 0.4); }
.particle:nth-child(5) { top: 50%; left: 45%; animation-duration: 16s; animation-delay: 3s; background: rgba(255, 105, 180, 0.4); }
.particle:nth-child(6) { top: 60%; left: 55%; animation-duration: 14s; animation-delay: 5s; background: rgba(64, 224, 208, 0.4); }
.particle:nth-child(7) { top: 70%; left: 65%; animation-duration: 22s; animation-delay: 2s; background: rgba(147, 112, 219, 0.4); }
.particle:nth-child(8) { top: 80%; left: 75%; animation-duration: 13s; animation-delay: 4s; background: rgba(255, 165, 0, 0.4); }
.particle:nth-child(9) { top: 90%; left: 85%; animation-duration: 17s; animation-delay: 1s; background: rgba(176, 224, 230, 0.4); }
.particle:nth-child(10) { top: 15%; left: 95%; animation-duration: 19s; animation-delay: 3s; background: rgba(240, 230, 140, 0.4); }
.particle:nth-child(11) { top: 25%; left: 85%; animation-duration: 11s; animation-delay: 5s; background: rgba(255, 182, 193, 0.4); }
.particle:nth-child(12) { top: 35%; left: 75%; animation-duration: 21s; animation-delay: 0s; background: rgba(173, 216, 230, 0.4); }
.particle:nth-child(13) { top: 45%; left: 65%; animation-duration: 18s; animation-delay: 2s; background: rgba(255, 192, 203, 0.4); }
.particle:nth-child(14) { top: 55%; left: 55%; animation-duration: 15s; animation-delay: 4s; background: rgba(144, 238, 144, 0.4); }
.particle:nth-child(15) { top: 65%; left: 45%; animation-duration: 12s; animation-delay: 1s; background: rgba(255, 215, 0, 0.4); }
.particle:nth-child(16) { top: 75%; left: 35%; animation-duration: 20s; animation-delay: 3s; background: rgba(255, 105, 180, 0.4); }
.particle:nth-child(17) { top: 85%; left: 25%; animation-duration: 16s; animation-delay: 5s; background: rgba(64, 224, 208, 0.4); }
.particle:nth-child(18) { top: 95%; left: 15%; animation-duration: 14s; animation-delay: 2s; background: rgba(147, 112, 219, 0.4); }
.particle:nth-child(19) { top: 5%; left: 5%; animation-duration: 22s; animation-delay: 4s; background: rgba(255, 165, 0, 0.4); }
.particle:nth-child(20) { top: 15%; left: 15%; animation-duration: 13s; animation-delay: 1s; background: rgba(176, 224, 230, 0.4); }
@keyframes particle-float-left {
  0% {
    transform: translateX(0) translateY(0);
    opacity: 0.3;
  }
  50% {
    transform: translateX(-50px) translateY(-100px);
    opacity: 0.8;
  }
  100% {
    transform: translateX(-100px) translateY(-200px);
    opacity: 0;
  }
}
@keyframes particle-float-right {
  0% {
    transform: translateX(0) translateY(0);
    opacity: 0.3;
  }
  50% {
    transform: translateX(50px) translateY(-100px);
    opacity: 0.8;
  }
  100% {
    transform: translateX(100px) translateY(-200px);
    opacity: 0;
  }
}
/* 现代化顶部导�?*/
.header {
  background: rgba(0, 30, 60, 0.25);
  backdrop-filter: blur(20px) saturate(1.4);
  border-bottom: 1px solid rgba(0, 162, 255, 0.2);
  position: static;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 162, 255, 0.15);
  padding: 0.5rem 0 0.25rem 0;
}
.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}
.logo h1 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #ffffff;
  position: relative;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 20px rgba(255, 255, 255, 0.4);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.9));
}
.nav {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}
.nav-link {
  text-decoration: none;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  font-size: 1.2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.5rem 1rem;
  border-radius: 0;
  position: relative;
  background: transparent;
  backdrop-filter: none;
  border: none;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  box-shadow: none;
}
.nav-link:hover {
  color: white;
  background: transparent;
  transform: translateY(-2px);
  border: none;
  box-shadow: none;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 15px rgba(255, 255, 255, 0.8);
}
/* 精致的英雄区�?*/
.hero-section {
  min-height: 80vh;
  padding: 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.6) 100%);
  z-index: 2;
  pointer-events: none;
}
.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}
.hero-content {
  position: relative;
  z-index: 10;
  padding: 2rem;
  width: 100%;
  max-width: 1200px;
}
.hero-title {
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  /*background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%);*/
  background: #fff8f4;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  /*text-shadow:*/
  /*  0 0 40px rgba(255, 255, 255, 0.8),*/
  /*  0 4px 8px rgba(0, 0, 0, 0.8),*/
  /*  0 8px 16px rgba(0, 0, 0, 0.6),*/
  /*  0 12px 24px rgba(0, 0, 0, 0.4);*/
  position: relative;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.9));
}
.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.95);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 4px 8px rgba(0, 0, 0, 0.6),
    0 0 20px rgba(255, 255, 255, 0.3);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.9));
}
.hero-description {
  font-size: 1.125rem;
  max-width: 700px;
  margin: 0 auto 3rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.7;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 10px rgba(255, 255, 255, 0.3);
}
.hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}
/* 现代按钮设计 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  font-size: 10rem;
  font-weight: 600;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
}
.btn-primary {
  background: linear-gradient(135deg, rgba(0, 162, 255, 0.9) 0%, rgba(255, 105, 180, 0.8) 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 8px 24px rgba(0, 162, 255, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}
.btn-primary:hover {
  background: linear-gradient(135deg, rgba(0, 162, 255, 1) 0%, rgba(255, 105, 180, 0.9) 100%);
  transform: translateY(-3px);
  box-shadow:
    0 12px 36px rgba(0, 162, 255, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}
.btn-secondary {
  background: rgba(0, 20, 40, 0.4);
  color: white;
  border: 2px solid rgba(0, 162, 255, 0.6);
  backdrop-filter: blur(15px) saturate(1.3);
  box-shadow:
    0 8px 24px rgba(0, 162, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}
.btn-secondary:hover {
  background: rgba(0, 162, 255, 0.2);
  border-color: rgba(0, 162, 255, 0.8);
  transform: translateY(-3px);
  box-shadow:
    0 12px 36px rgba(0, 162, 255, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
}
/* 精致的粒子效�?*/
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 100px 100px, 200px 200px;
  background-position: 0 0, 50px 50px;
  animation: particles-drift 20s linear infinite;
  opacity: 0.6;
}
@keyframes particles-drift {
  0% { transform: translate(0, 0); }
  100% { transform: translate(-100px, -100px); }
}
.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: grid-float 8s ease-in-out infinite;
}
@keyframes grid-float {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.02); }
}
.floating-element {
  position: absolute;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  animation: float-elegant 15s ease-in-out infinite;
}
.floating-element:nth-child(1) {
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}
.floating-element:nth-child(2) {
  top: 20%;
  right: 15%;
  animation-delay: -2s;
  border-radius: 50%;
  width: 60px;
  height: 60px;
}
.floating-element:nth-child(3) {
  top: 60%;
  left: 5%;
  animation-delay: -4s;
  width: 100px;
  height: 40px;
}
.floating-element:nth-child(4) {
  top: 70%;
  right: 10%;
  animation-delay: -6s;
  border-radius: 50%;
  width: 120px;
  height: 120px;
}
.floating-element:nth-child(5) {
  top: 40%;
  left: 50%;
  animation-delay: -8s;
  width: 50px;
  height: 50px;
}
.floating-element:nth-child(6) {
  top: 15%;
  left: 70%;
  animation-delay: -10s;
  border-radius: 50%;
  width: 90px;
  height: 90px;
}
@keyframes float-elegant {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 0.8;
  }
}
/* 移动光点效果 */
.moving-lights {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}
.light-orb {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
  animation: light-drift linear infinite;
}
.light-orb:nth-child(1) {
  top: 10%;
  left: 5%;
  animation-duration: 12s;
  animation-delay: 0s;
}
.light-orb:nth-child(2) {
  top: 20%;
  left: 15%;
  animation-duration: 15s;
  animation-delay: 2s;
}
.light-orb:nth-child(3) {
  top: 30%;
  left: 25%;
  animation-duration: 10s;
  animation-delay: 4s;
}
.light-orb:nth-child(4) {
  top: 40%;
  left: 35%;
  animation-duration: 18s;
  animation-delay: 1s;
}
.light-orb:nth-child(5) {
  top: 50%;
  left: 45%;
  animation-duration: 14s;
  animation-delay: 3s;
}
.light-orb:nth-child(6) {
  top: 60%;
  left: 55%;
  animation-duration: 16s;
  animation-delay: 5s;
}
.light-orb:nth-child(7) {
  top: 70%;
  left: 65%;
  animation-duration: 11s;
  animation-delay: 2s;
}
.light-orb:nth-child(8) {
  top: 80%;
  left: 75%;
  animation-duration: 13s;
  animation-delay: 6s;
}
.light-orb:nth-child(9) {
  top: 15%;
  left: 85%;
  animation-duration: 17s;
  animation-delay: 1s;
}
.light-orb:nth-child(10) {
  top: 35%;
  left: 95%;
  animation-duration: 19s;
  animation-delay: 4s;
}
@keyframes light-drift {
  0% {
    transform: translateX(0) translateY(0);
    opacity: 0.3;
  }
  25% {
    transform: translateX(100px) translateY(-50px);
    opacity: 0.8;
  }
  50% {
    transform: translateX(-50px) translateY(-100px);
    opacity: 0.6;
  }
  75% {
    transform: translateX(-100px) translateY(50px);
    opacity: 0.9;
  }
  100% {
    transform: translateX(0) translateY(0);
    opacity: 0.3;
  }
}
/* 优雅的区块样�?*/
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}
.section {
  padding: 6rem 0;
  position: relative;
}
.section-title {
  text-align: center;
  margin-bottom: 4rem;
}
.section-title h2 {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-shadow:
    0 0 30px rgba(255, 255, 255, 0.6),
    0 4px 8px rgba(0, 0, 0, 0.8),
    0 8px 16px rgba(0, 0, 0, 0.6);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.9));
}
.section-title p {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 10px rgba(255, 255, 255, 0.2);
  background: rgba(0, 20, 40, 0.3);
  backdrop-filter: blur(12px) saturate(1.2);
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid rgba(0, 162, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 162, 255, 0.15);
}
/* 演讲者区块美�?*/
.speakers-section {
  background: #ffffff;
  backdrop-filter: none;
  position: relative;
  overflow: hidden;
}
.speakers-section::before {
  display: none;
}
.speakers-section .container {
  position: relative;
  z-index: 2;
}
.speakers-section .section-title h2 {
  background: linear-gradient(135deg, #333333 0%, #666666 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
  filter: none;
}
.speakers-section .section-title p {
  color: #666666;
  text-shadow: none;
  background: #ffffff;
  backdrop-filter: none;
  border: none;
  box-shadow: none;
}
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.speaker-card {
  background: transparent;
  backdrop-filter: none;
  border: none;
  border-radius: 0;
  padding: 1rem;
  text-align: center;
  box-shadow: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
}
.speaker-card::before {
  display: none;
}
.speaker-card:hover::before {
  display: none;
}
.speaker-card:hover {
  transform: translateY(-8px);
  box-shadow: none;
  border-color: transparent;
  background: transparent;
}
.speaker-avatar {
  margin-bottom: 1.5rem;
}
.speaker-avatar img {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  object-fit: cover;
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}
.speaker-card:hover .speaker-avatar img {
  border-color: transparent;
  transform: scale(1.05);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.4);
}
.speaker-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 0.3rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
.speaker-name a {
  color: inherit;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.speaker-name a:hover {
  color: #007bff;
  text-shadow: none;
  transform: translateY(-2px);
}
.speaker-name a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #007bff 0%, #6c757d 100%);
  transition: width 0.3s ease;
  border-radius: 1px;
}
.speaker-name a:hover::after {
  width: 100%;
}
.speaker-affiliation {
  color: #666666;
  font-weight: 500;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
.speaker-title {
  color: #777777;
  font-size: 0.9rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
/* 会议安排区块美化 */
.schedule-section {
  background: #ffffff;
  backdrop-filter: none;
  position: relative;
  overflow: hidden;
}
.schedule-section::before {
  display: none;
}
.schedule-section .container {
  position: relative;
  z-index: 2;
}
.schedule-section .section-title h2 {
  background: linear-gradient(135deg, #333333 0%, #666666 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
  filter: none;
}
.schedule-section .section-title p {
  color: #666666;
  text-shadow: none;
  background: #ffffff;
  backdrop-filter: none;
  border: none;
  box-shadow: none;
}
.timeline {
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 30px;
  top: 2rem;
  bottom: 2rem;
  width: 2px;
  background: var(--gradient-primary);
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.3);
}
.timeline-item {
  display: flex;
  margin-bottom: 2rem;
  position: relative;
  align-items: center;
}
.timeline-time {
  width: 180px;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
  text-align: center;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  position: relative;
  background: #007bff;
  backdrop-filter: none;
  border: 1px solid #007bff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
  margin-right: 2rem;
  transition: all 0.3s ease;
  text-shadow: none;
}
.timeline-time:hover {
  transform: scale(1.02);
  background: #0056b3;
  border-color: #0056b3;
}
.timeline-content {
  flex: 1;
  background: #ffffff;
  backdrop-filter: none;
  border: 1px solid #e9ecef;
  padding: 1rem;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.timeline-content:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: #007bff;
  background: #ffffff;
}
.timeline-content::before {
  content: '';
  position: absolute;
  left: -21px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: #007bff;
  border-radius: 50%;
  border: 3px solid #ffffff;
  box-shadow: 0 0 0 3px #e9ecef;
  z-index: 2;
}
.timeline-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 0.75rem;
  text-shadow: none;
}
.timeline-content p {
  color: #666666;
  margin-bottom: 0.75rem;
  line-height: 1.6;
  font-size: 1.05rem;
  text-shadow: none;
}
.timeline-speaker {
  display: inline-block;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  background: #28a745;
  backdrop-filter: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid #28a745;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
  text-shadow: none;
}
/* 组织者区块美�?*/
.organizers-section {
  background: #ffffff;
  backdrop-filter: none;
  position: relative;
  overflow: hidden;
}
.organizers-section::before {
  display: none;
}
.organizers-section .container {
  position: relative;
  z-index: 2;
}
.organizers-section .section-title h2 {
  background: linear-gradient(135deg, #333333 0%, #666666 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
  filter: none;
}
.organizers-section .section-title p {
  color: #666666;
  text-shadow: none;
  background: #ffffff;
  backdrop-filter: none;
  border: none;
  box-shadow: none;
}
.organizers-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  justify-items: center;
}
.organizer-card {
  width: 100%;
  max-width: 280px;
  background: transparent;
  backdrop-filter: none;
  border: none;
  border-radius: 0;
  padding: 1rem;
  text-align: center;
  box-shadow: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
}
.organizer-card::before {
  display: none;
}
.organizer-card:hover::before {
  display: none;
}
.organizer-card:hover {
  transform: translateY(-8px);
  box-shadow: none;
  border-color: transparent;
  background: transparent;
}
.organizer-avatar {
  margin-bottom: 1.5rem;
}
.organizer-avatar img {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  object-fit: cover;
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}
.organizer-card:hover .organizer-avatar img {
  border-color: transparent;
  transform: scale(1.05);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.4);
}
.organizer-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
.organizer-name a {
  color: inherit;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.organizer-name a:hover {
  color: #007bff;
  text-shadow: none;
  transform: translateY(-2px);
}
.organizer-name a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #007bff 0%, #6c757d 100%);
  transition: width 0.3s ease;
  border-radius: 1px;
}
.organizer-name a:hover::after {
  width: 100%;
}
.organizer-affiliation {
  color: #666666;
  font-weight: 500;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
.organizer-role {
  color: #777777;
  font-size: 0.95rem;
  position: relative;
  z-index: 10;
  text-shadow: none;
}
/* 第二行的三个元素 */
.organizer-card:nth-child(5) {
  margin-left: calc(12.5% + 1rem);
}

@media (max-width: 1024px) {
  .organizer-card {
    flex: 0 0 calc(50% - 1rem);
  }

  .organizer-card:nth-child(5) {
    margin-left: 0;
  }
}

@media (max-width: 640px) {
  .organizer-card {
    flex: 0 0 100%;
  }

  .organizer-card:nth-child(5) {
    margin-left: 0;
  }
}
/* 投稿须知区块美化 */
.submission-section {
  padding: 60px 0;
  background-color: #fff;
}

.main-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 50px;
  color: #333;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

.section-block {
  margin-bottom: 30px;
}

.section-block h3 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
}

.section-block p {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #666;
}

.dates-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.date-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 1.1rem;
  line-height: 1.6;
}

.date-label {
  color: #333;
  white-space: nowrap;
}

.date-value {
  color: #666;
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .main-title {
    font-size: 2rem;
  }

  .section-block h3 {
    font-size: 1.5rem;
  }
}
/* 联系方式区块美化 */
.contact-section {
  background: #ffffff;
  backdrop-filter: none;
  color: #333333;
  padding: 4rem 0;
  position: relative;
  overflow: hidden;
  border-top: 1px solid #e9ecef;
}
.contact-section::before {
  display: none;
}
.contact-section .container {
  position: relative;
  z-index: 2;
}

/* 组织原则模块样式 */
.organizing-principles {
  max-width: 1000px;
  margin: 0 auto 4rem auto;
  background: #ffffff;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  text-align: left;
}

.organizing-principles h3 {
  font-size: 2.2rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 2rem;
  text-align: center;
}

/*
.organizing-principles h3::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(135deg, #007bff 0%, #6c757d 100%);
  border-radius: 2px;
}
*/

.principles-content {
  line-height: 1.8;
}

.principles-content p {
  color: #555555;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  text-align: justify;
}

.principles-content p:last-child {
  margin-bottom: 0;
}

.contact-content {
  display: block;
  max-width: 1000px;
  margin: 0 auto;
}
.contact-info {
  text-align: center;
  background: #ffffff;
  backdrop-filter: none;
  border: 1px solid #e9ecef;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}
.contact-info h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 2rem;
  text-shadow: none;
}
.contact-item {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  font-size: 1.2rem;
  white-space: nowrap;
  overflow-x: auto;
}
.contact-label {
  color: #666666;
  font-weight: 600;
  text-shadow: none;
}
.contact-value {
  color: #007bff;
  font-weight: 600;
  text-shadow: none;
  text-decoration: none;
  transition: color 0.3s ease;
  white-space: nowrap;
  display: inline-block;
}
.contact-value:hover {
  color: #0056b3;
}
/* 页脚美化 */
.footer {
  background: #f8f9fa;
  backdrop-filter: none;
  border-top: 1px solid #e9ecef;
  color: #666666;
  text-align: center;
  padding: 2.5rem 0;
  position: relative;
  text-shadow: none;
}
.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(135deg, #007bff 0%, #6c757d 100%);
  opacity: 0.6;
}
/* 响应式设?*/
@media (min-width: 769px) {
  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .header {
    padding: 0.5rem 0;
  }
  .logo {
    margin-left: -15rem;
  }
  .nav {
    justify-content: flex-end;
    margin-right: -15rem;
  }
  .timeline::before {
    left: 200px;
  }
  .timeline-item {
    flex-direction: row;
    align-items: center;
    margin-bottom: 2rem;
  }
  .timeline-time {
    width: 180px;
    margin-right: 2rem;
    margin-bottom: 0;
    margin-left: 0;
  }
  .timeline-content {
    margin-left: 0;
    width: auto;
    flex: 1;
  }
  .timeline-content::before {
    left: -21px;
  }
}
@media (max-width: 1024px) and (min-width: 769px) {
  .speakers-grid,
  .organizers-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }

  .organizing-principles {
    margin-bottom: 3rem;
    padding: 2rem 1.5rem;
  }

  .organizing-principles h3 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }

  .principles-content p {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }

  .header-content {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem 1.5rem;
  }
  .logo h1 {
    font-size: 1.25rem;
  }
  .nav {
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }
  .nav-link {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
  .hero-section {
    min-height: 80vh;
    padding: 0;
  }
  .hero-content {
    padding: 1rem;
  }
  .hero-title {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
  .hero-subtitle {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
  .hero-description {
    font-size: 1rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  .hero-actions {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
  .scroll-down-indicator {
    bottom: 60px;
    left: 50%;
    transform: translateX(-50%);
  }
  .scroll-arrow {
    font-size: 1.5rem;
  }
  .scroll-text {
    font-size: 1rem;
    font-weight: 500;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
    white-space: nowrap;
  }
  .section {
    padding: 4rem 0;
  }
  .section-title h2 {
    font-size: 2rem;
  }
  .speakers-grid,
  .organizers-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .timeline::before {
    left: 30px;
  }
  .timeline-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2.5rem;
  }
  .timeline-time {
    width: auto;
    margin-right: 0;
    margin-bottom: 1rem;
    margin-left: 60px;
  }
  .timeline-content {
    margin-left: 60px;
    width: calc(100% - 60px);
  }
  .timeline-content::before {
    left: -81px;
  }
  .submission-content,
  .contact-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  .date-grid {
    grid-template-columns: 1fr;
  }
  .personnel-item {
    padding: 0.75rem 1rem;
  }
  .university-logos {
    gap: 2rem;
    margin-bottom: 2rem;
  }
  .logo-item {
    width: 120px;
    height: 120px;
  }
  .contact-info {
    padding: 1.5rem;
  }
  .contact-info h3 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  .contact-item {
    font-size: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
}
@media (max-width: 480px) {
  .speakers-grid,
  .organizers-grid {
    grid-template-columns: 1fr;
  }
  .hero-section {
    min-height: 80vh;
  }
  .hero-content {
    padding: 0.75rem;
  }
  .hero-title {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }
  .hero-subtitle {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }
  .hero-description {
    font-size: 0.95rem;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }
  .section-title h2 {
    font-size: 1.75rem;
  }
  .btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    width: 100%;
    max-width: 240px;
  }
}
/* 滚动条样?*/
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: var(--gray-100);
}
::-webkit-scrollbar-thumb {
  background: var(--gray-400);
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--gray-500);
}
/* 选中文字样式 */
::selection {
  background: rgba(30, 64, 175, 0.2);
  color: var(--gray-900);
}
/* 波纹效果 */
.ripple-effects {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.ripple {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  animation: ripple-expand linear infinite;
}
.ripple:nth-child(1) {
  width: 300px;
  height: 300px;
  top: 25%;
  left: 30%;
  animation-duration: 8s;
  animation-delay: 0s;
  border-color: rgba(173, 216, 230, 0.1);
}
.ripple:nth-child(2) {
  width: 400px;
  height: 400px;
  top: 45%;
  left: 60%;
  animation-duration: 12s;
  animation-delay: 3s;
  border-color: rgba(255, 192, 203, 0.08);
}
.ripple:nth-child(3) {
  width: 250px;
  height: 250px;
  top: 70%;
  left: 20%;
  animation-duration: 10s;
  animation-delay: 6s;
  border-color: rgba(144, 238, 144, 0.09);
}
.ripple:nth-child(4) {
  width: 350px;
  height: 350px;
  top: 10%;
  left: 70%;
  animation-duration: 15s;
  animation-delay: 2s;
  border-color: rgba(255, 215, 0, 0.07);
}
@keyframes ripple-expand {
  0% {
    transform: scale(0.5);
    opacity: 0.8;
  }
  50% {
    transform: scale(1);
    opacity: 0.4;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}
/* 交互元素样式 */
.interactive-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  z-index: 10;
}
/* 鼠标轨迹 */
.mouse-trail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.trail-particle {
  position: fixed;
  width: 6px;
  height: 6px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(100, 200, 255, 0.6) 50%, transparent 100%);
  border-radius: 50%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px rgba(100, 200, 255, 0.6);
}
/* 交互泡泡 */
.interactive-bubbles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.bubble {
  position: absolute;
  cursor: pointer;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, rgba(173, 216, 230, 0.2) 40%, rgba(100, 200, 255, 0.1) 100%);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: bubble-float 4s ease-in-out infinite;
}
.bubble:hover {
  transform: scale(1.2);
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 192, 203, 0.3) 40%, rgba(255, 105, 180, 0.2) 100%);
  box-shadow: 0 0 30px rgba(255, 105, 180, 0.4);
}
.bubble-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.4) 0%, transparent 50%);
  opacity: 0.6;
}
@keyframes bubble-float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}
/* 交互星星 */
.interactive-stars {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.star {
  position: absolute;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.star-inner {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.6);
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
  transition: all 0.4s ease;
}
.star-1 { top: 15%; left: 20%; animation: star-twinkle 3s ease-in-out infinite; animation-delay: 0s; }
.star-2 { top: 25%; left: 70%; animation: star-twinkle 3.5s ease-in-out infinite; animation-delay: 0.5s; }
.star-3 { top: 40%; left: 10%; animation: star-twinkle 4s ease-in-out infinite; animation-delay: 1s; }
.star-4 { top: 50%; left: 85%; animation: star-twinkle 3.2s ease-in-out infinite; animation-delay: 1.5s; }
.star-5 { top: 65%; left: 30%; animation: star-twinkle 3.8s ease-in-out infinite; animation-delay: 2s; }
.star-6 { top: 75%; left: 80%; animation: star-twinkle 4.2s ease-in-out infinite; animation-delay: 0.2s; }
.star-7 { top: 35%; left: 50%; animation: star-twinkle 3.6s ease-in-out infinite; animation-delay: 0.8s; }
.star-8 { top: 80%; left: 15%; animation: star-twinkle 4.5s ease-in-out infinite; animation-delay: 1.2s; }
.star-9 { top: 20%; left: 90%; animation: star-twinkle 3.3s ease-in-out infinite; animation-delay: 1.8s; }
.star-10 { top: 55%; left: 60%; animation: star-twinkle 3.9s ease-in-out infinite; animation-delay: 0.3s; }
.star-11 { top: 10%; left: 45%; animation: star-twinkle 4.1s ease-in-out infinite; animation-delay: 1.4s; }
.star-12 { top: 85%; left: 55%; animation: star-twinkle 3.7s ease-in-out infinite; animation-delay: 0.7s; }
@keyframes star-twinkle {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
/* 点击波纹区域 */
.click-ripple-area {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}
.click-ripple {
  position: absolute;
  width: 100px;
  height: 100px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: click-ripple-expand 2s ease-out;
  pointer-events: none;
}
@keyframes click-ripple-expand {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}
/* 爆炸粒子 */
.explosion-particle {
  position: fixed;
  width: 8px;
  height: 8px;
  background: radial-gradient(circle, #ff6b6b 0%, #ffd93d 50%, transparent 100%);
  border-radius: 50%;
  pointer-events: none;
  animation: explosion-fly 1s ease-out forwards;
  z-index: 9999;
}
@keyframes explosion-fly {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--vx), var(--vy)) scale(0);
    opacity: 0;
  }
}
/* 动态连接线 */
.network-connections {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.connection-svg {
  width: 100%;
  height: 100%;
}
.connection-line {
  stroke: url(#connectionGradient1);
  stroke-width: 1;
}
.line-1 { d: 'M 10% 20% Q 80% 50%, 90% 85%'; }
.line-2 { d: 'M 20% 70% Q 90% 10%, 85% 77%'; }
.line-3 { d: 'M 5% 40% Q 60% 30%, 45% 60%'; }
.line-4 { d: 'M 45% 60% Q 85% 85%, 95% 77%'; }
.line-5 { d: 'M 25% 25% Q 40% 15%, 70% 60%'; }
.line-6 { d: 'M 70% 60% Q 95% 55%, 85% 77%'; }
.line-7 { d: 'M 12% 33% Q 52% 77%, 68% 8%'; }
.line-8 { d: 'M 68% 8% Q 85% 77%, 95% 77%'; }
/* 脉冲环形�?*/
.pulse-rings {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.pulse-ring {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: pulse-ring-animation 4s linear infinite;
}
.pulse-ring:nth-child(1) { width: 150px; height: 150px; top: 20%; left: 25%; animation-delay: 0s; }
.pulse-ring:nth-child(2) { width: 200px; height: 200px; top: 60%; left: 70%; animation-delay: 2s; }
.pulse-ring:nth-child(3) { width: 120px; height: 120px; top: 40%; left: 15%; animation-delay: 4s; }
.pulse-ring:nth-child(4) { width: 180px; height: 180px; top: 15%; left: 80%; animation-delay: 6s; }
.pulse-ring:nth-child(5) { width: 100px; height: 100px; top: 75%; left: 40%; animation-delay: 8s; }
.pulse-ring:nth-child(6) { width: 220px; height: 220px; top: 50%; left: 50%; animation-delay: 10s; }
.pulse-ring:nth-child(7) { width: 120px; height: 120px; top: 70%; left: 30%; animation-delay: 12s; }
.pulse-ring:nth-child(8) { width: 180px; height: 180px; top: 85%; left: 65%; animation-delay: 14s; }
@keyframes pulse-ring-animation {
  0% { transform: scale(1); opacity: 0.1; }
  50% { transform: scale(1.3); opacity: 0.3; }
  100% { transform: scale(1); opacity: 0.1; }
}
/* 数据流粒�?*/
.data-stream {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.data-particle {
  position: absolute;
  width: 3px;
  height: 3px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: data-stream-animation 2s linear infinite;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
}
.data-particle:nth-child(odd) { animation-name: data-stream-left; }
.data-particle:nth-child(even) { animation-name: data-stream-right; }
.data-particle:nth-child(1) { top: 10%; left: 5%; animation-duration: 15s; animation-delay: 0s; background: rgba(0, 162, 255, 0.8); box-shadow: 0 0 8px rgba(0, 162, 255, 0.6); }
.data-particle:nth-child(2) { top: 20%; left: 15%; animation-duration: 18s; animation-delay: 2s; background: rgba(255, 255, 255, 0.6); }
.data-particle:nth-child(3) { top: 30%; left: 25%; animation-duration: 12s; animation-delay: 4s; background: rgba(255, 165, 0, 0.7); box-shadow: 0 0 8px rgba(255, 165, 0, 0.5); }
.data-particle:nth-child(4) { top: 40%; left: 35%; animation-duration: 20s; animation-delay: 1s; background: rgba(144, 238, 144, 0.8); box-shadow: 0 0 8px rgba(144, 238, 144, 0.6); }
.data-particle:nth-child(5) { top: 50%; left: 45%; animation-duration: 16s; animation-delay: 3s; background: rgba(255, 105, 180, 0.7); box-shadow: 0 0 8px rgba(255, 105, 180, 0.5); }
.data-particle:nth-child(6) { top: 60%; left: 55%; animation-duration: 14s; animation-delay: 5s; background: rgba(64, 224, 208, 0.8); box-shadow: 0 0 8px rgba(64, 224, 208, 0.6); }
.data-particle:nth-child(7) { top: 70%; left: 65%; animation-duration: 22s; animation-delay: 2s; background: rgba(147, 112, 219, 0.6); box-shadow: 0 0 8px rgba(147, 112, 219, 0.4); }
.data-particle:nth-child(8) { top: 80%; left: 75%; animation-duration: 13s; animation-delay: 4s; background: rgba(255, 215, 0, 0.7); box-shadow: 0 0 8px rgba(255, 215, 0, 0.5); }
.data-particle:nth-child(9) { top: 90%; left: 85%; animation-duration: 17s; animation-delay: 1s; background: rgba(176, 224, 230, 0.6); }
.data-particle:nth-child(10) { top: 15%; left: 95%; animation-duration: 19s; animation-delay: 3s; background: rgba(240, 230, 140, 0.7); }
.data-particle:nth-child(11) { top: 25%; left: 85%; animation-duration: 11s; animation-delay: 5s; background: rgba(255, 182, 193, 0.6); }
.data-particle:nth-child(12) { top: 35%; left: 75%; animation-duration: 21s; animation-delay: 0s; background: rgba(0, 162, 255, 0.8); box-shadow: 0 0 8px rgba(0, 162, 255, 0.6); }
.data-particle:nth-child(13) { top: 45%; left: 65%; animation-duration: 18s; animation-delay: 2s; background: rgba(255, 255, 255, 0.6); }
.data-particle:nth-child(14) { top: 55%; left: 55%; animation-duration: 15s; animation-delay: 4s; background: rgba(255, 165, 0, 0.7); box-shadow: 0 0 8px rgba(255, 165, 0, 0.5); }
.data-particle:nth-child(15) { top: 65%; left: 45%; animation-duration: 12s; animation-delay: 1s; background: rgba(144, 238, 144, 0.8); box-shadow: 0 0 8px rgba(144, 238, 144, 0.6); }
.data-particle:nth-child(16) { top: 75%; left: 35%; animation-duration: 20s; animation-delay: 3s; background: rgba(255, 105, 180, 0.7); box-shadow: 0 0 8px rgba(255, 105, 180, 0.5); }
.data-particle:nth-child(17) { top: 85%; left: 25%; animation-duration: 16s; animation-delay: 5s; background: rgba(64, 224, 208, 0.8); box-shadow: 0 0 8px rgba(64, 224, 208, 0.6); }
.data-particle:nth-child(18) { top: 95%; left: 15%; animation-duration: 14s; animation-delay: 2s; background: rgba(147, 112, 219, 0.6); box-shadow: 0 0 8px rgba(147, 112, 219, 0.4); }
.data-particle:nth-child(19) { top: 5%; left: 5%; animation-duration: 22s; animation-delay: 4s; background: rgba(255, 215, 0, 0.7); box-shadow: 0 0 8px rgba(255, 215, 0, 0.5); }
.data-particle:nth-child(20) { top: 15%; left: 15%; animation-duration: 13s; animation-delay: 1s; background: rgba(176, 224, 230, 0.6); }
.data-particle:nth-child(21) { top: 35%; left: 5%; animation-duration: 17s; animation-delay: 3s; background: rgba(0, 162, 255, 0.8); box-shadow: 0 0 8px rgba(0, 162, 255, 0.6); }
.data-particle:nth-child(22) { top: 55%; left: 25%; animation-duration: 19s; animation-delay: 5s; background: rgba(255, 165, 0, 0.7); box-shadow: 0 0 8px rgba(255, 165, 0, 0.5); }
.data-particle:nth-child(23) { top: 75%; left: 45%; animation-duration: 11s; animation-delay: 0s; background: rgba(144, 238, 144, 0.8); box-shadow: 0 0 8px rgba(144, 238, 144, 0.6); }
.data-particle:nth-child(24) { top: 95%; left: 65%; animation-duration: 21s; animation-delay: 2s; background: rgba(255, 105, 180, 0.7); box-shadow: 0 0 8px rgba(255, 105, 180, 0.5); }
.data-particle:nth-child(25) { top: 12%; left: 78%; animation-duration: 18s; animation-delay: 4s; background: rgba(64, 224, 208, 0.8); box-shadow: 0 0 8px rgba(64, 224, 208, 0.6); }
@keyframes data-stream-left {
  0% { transform: translateX(0) translateY(0) scale(0.5); opacity: 0.3; }
  25% { transform: translateX(-30px) translateY(-50px) scale(1); opacity: 0.8; }
  50% { transform: translateX(-60px) translateY(-100px) scale(1.2); opacity: 1; }
  75% { transform: translateX(-90px) translateY(-150px) scale(0.8); opacity: 0.6; }
  100% { transform: translateX(-120px) translateY(-200px) scale(0.3); opacity: 0; }
}
@keyframes data-stream-right {
  0% { transform: translateX(0) translateY(0) scale(0.5); opacity: 0.3; }
  25% { transform: translateX(30px) translateY(-50px) scale(1); opacity: 0.8; }
  50% { transform: translateX(60px) translateY(-100px) scale(1.2); opacity: 1; }
  75% { transform: translateX(90px) translateY(-150px) scale(0.8); opacity: 0.6; }
  100% { transform: translateX(120px) translateY(-200px) scale(0.3); opacity: 0; }
}
/* 隐藏所有背景动态效�?*/
.global-background-effects {
  display: none;
}
.floating-orbs {
  display: none;
}
.aurora-lights {
  display: none;
}
.geometric-shapes {
  display: none;
}
.shooting-stars {
  display: none;
}
.breathing-rings {
  display: none;
}
.moving-particles {
  display: none;
}
.ripple-effects {
  display: none;
}
.interactive-elements {
  display: none;
}
.network-connections {
  display: none;
}
.pulse-rings {
  display: none;
}
.data-stream {
  display: none;
}
.particles {
  display: none;
}
.grid-overlay {
  display: none;
}
.floating-elements {
  display: none;
}
.moving-lights {
  display: none;
}
/* 向下滑动提示箭头 */
.scroll-down-indicator {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  cursor: default;
  transition: all 0.3s ease;
  animation: scroll-bounce 2s ease-in-out infinite;
  z-index: 20;
}
.scroll-down-indicator:hover {
  color: #ffffff;
  transform: scale(1.1);
}
.scroll-arrow {
  font-size: 2rem;
  margin-bottom: 0.25rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8));
}
.scroll-arrow span {
  display: block;
  animation: arrow-bounce 2s ease-in-out infinite;
}
.scroll-text {
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}
@keyframes scroll-bounce {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-10px);
    opacity: 1;
  }
}
@keyframes arrow-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(8px);
  }
}
/* 校徽展示 */
.university-logos {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 3rem;
  margin-bottom: 3rem;
  padding: 2rem 0;
}
.logo-item {
  width: 160px;
  height: 160px;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  padding: 8px;
}
.logo-item:hover {
  /* 去掉所有hover特效 */
}
.university-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}
/* 联系方式调整 */
.contact-content {
  display: block;
  max-width: 1000px;
  margin: 0 auto;
}
.contact-info {
  text-align: center;
  background: #ffffff;
  backdrop-filter: none;
  border: 1px solid #e9ecef;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}
.contact-info h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #333333;
  margin-bottom: 2rem;
  text-shadow: none;
}
.contact-item {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  font-size: 1.2rem;
}
.contact-label {
  color: #666666;
  font-weight: 600;
  text-shadow: none;
}
.contact-value {
  color: #007bff;
  font-weight: 600;
  text-shadow: none;
  text-decoration: none;
  transition: color 0.3s ease;
}
.contact-value:hover {
  color: #0056b3;
}
@media (max-width: 768px) {
  .university-logos {
    gap: 2rem;
    margin-bottom: 2rem;
  }
  .logo-item {
    width: 120px;
    height: 120px;
  }
  .contact-info {
    padding: 1.5rem;
  }
  .contact-info h3 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  .contact-item {
    font-size: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
}
/* Program Committee区块美化 */
.program-committee-section {
  background: #ffffff;
  position: relative;
  overflow: hidden;
  padding: 5rem 0;
}

.program-committee-section .container {
  position: relative;
  z-index: 2;
}

.program-committee-section .section-title {
  text-align: center;
  margin-bottom: 4rem;
}

.program-committee-section .section-title h2 {
  background: linear-gradient(135deg, #333333 0%, #666666 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
  filter: none;
  position: relative;
}

/*
.program-committee-section .section-title h2::after {
  content: '';
  position: absolute;
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(135deg, #007bff 0%, #6c757d 100%);
  border-radius: 2px;
}
*/

.committee-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.committee-member {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 1.2rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 1rem;
  color: #444;
  font-weight: 500;
  line-height: 1.5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.committee-member::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #007bff 0%, #6c757d 100%);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.committee-member:hover::before {
  transform: scaleY(1);
}

.committee-member:hover {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 123, 255, 0.3);
}

/*.committee-member:nth-child(even) {*/
/*  background: rgba(248, 249, 250, 0.8);*/
/*}*/

/*.committee-member:nth-child(even):hover {*/
/*  background: rgba(248, 249, 250, 0.95);*/
/*}*/

@media (max-width: 768px) {
  .committee-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }

  .committee-member {
    padding: 1rem 1.2rem;
    font-size: 0.95rem;
  }

  .program-committee-section {
    padding: 4rem 0;
  }

  .program-committee-section .section-title {
    margin-bottom: 3rem;
  }
}

@media (max-width: 480px) {
  .committee-grid {
    grid-template-columns: 1fr;
  }

  .committee-member {
    padding: 0.9rem 1rem;
    font-size: 0.9rem;
  }

  .program-committee-section {
    padding: 3rem 0;
  }
}

/* 联系方式注脚样式 */
.contact-footnote {
  text-align: center;
  margin-top: 2rem;
  padding: 1rem 0;
}

.contact-footnote p {
  font-size: 0.9rem;
  font-weight: 700;
  color: #666666;
  margin: 0;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .contact-footnote p {
    font-size: 0.8rem;
    padding: 0 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue